import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanDeactivateFn, ResolveFn, Routes } from '@angular/router';
import { ShellAuthPage } from '@raxapp/auth/auth-page.component';
import { MainContainerComponent } from '@raxapp/layout/main-container/main-container.component';
import { ActiveTenantLocationStore } from '@raxapp/multi-tenants/ActiveTenantLocation.store';
import { MultiTenantStore } from '@raxapp/multi-tenants/MultiTenant.store';
import { MultiTenantLocationListPage } from '@raxapp/multi-tenants/select-location/location-list/location-list-page.component';
import { MultiTenantTenantListPage } from '@raxapp/multi-tenants/select-tenant/tenant-list/tenant-list-page.component';
import { LoggedOutPage } from '@raxapp/shell/pages/logged-out/logged-out-page.component';
import { ShellLoadingPage } from '@raxshell/pages/loading/loading-page.component';
import { SetDebugSettingsPage } from '../debugging/set-debug-settings/set-debug-settings-page.component';
import { ErrorPage } from '../error-handling/show-error-pages/error-page/error-page.component';
import { LocationHomePage } from '../location-home-page/location-home-page.component';
import { LoginFailedPage } from '../shell/pages/login-failed/login-failed-page.component';

const tenantResolver: ResolveFn<void> = (route: ActivatedRouteSnapshot) => {
    const multiTenantStore = inject(MultiTenantStore);
    const activeTenantLocationStore = inject(ActiveTenantLocationStore);
    const tenantIdCode = route.paramMap.get('tenantIdCode');

    if (tenantIdCode === null) {
        throw new Error('tenantIdCode undefined');
    }

    const activeTenant = multiTenantStore.tenants().find((tenant) => tenant.tenantIdCode === tenantIdCode);
    activeTenantLocationStore.setActiveTenant(activeTenant);
};

const locationResolver: ResolveFn<void> = (route: ActivatedRouteSnapshot) => {
    const multiTenantStore = inject(MultiTenantStore);
    const tenantIdCode = route.paramMap.get('tenantIdCode');
    const locationIdCode = route.paramMap.get('locationIdCode');

    if (tenantIdCode === null) {
        throw new Error('tenantIdCode undefined');
    }

    if (locationIdCode === null) {
        throw new Error('locationIdCode undefined');
    }

    const activeLocation = multiTenantStore
        .tenants()
        .find((tenant) => tenant.tenantIdCode === tenantIdCode)
        ?.locations.find((location) => location.locationIdCode === locationIdCode);

    inject(ActiveTenantLocationStore).setActiveLocation(activeLocation);
};

const clearTenantCanDeactivateGuard: CanDeactivateFn<void> = () => {
    inject(ActiveTenantLocationStore).clearActiveTenant();
    return true;
};

const clearLocationCanDeactivateGuard: CanDeactivateFn<void> = () => {
    inject(ActiveTenantLocationStore).clearActiveLocation();
    return true;
};

export const AppRoutes: Routes = [
    {
        path: 'loading',
        component: ShellLoadingPage,
    },
    {
        path: 'auth',
        component: ShellAuthPage,
    },
    {
        path: 'logout',
        component: LoggedOutPage,
    },
    {
        path: 'login-failed',
        component: LoginFailedPage,
    },
    {
        path: 'error',
        component: ErrorPage,
    },
    {
        path: 'tenant-list',
        component: MainContainerComponent,
        children: [
            {
                path: '',
                component: MultiTenantTenantListPage,
            },
        ],
    },
    {
        path: 't/:tenantIdCode',
        component: MainContainerComponent,
        resolve: { tenant: tenantResolver },
        canDeactivate: [clearTenantCanDeactivateGuard],
        children: [
            { path: '', redirectTo: 'location', pathMatch: 'full' },
            {
                path: 'location',
                children: [
                    { path: '', redirectTo: 'location-list', pathMatch: 'full' },
                    {
                        path: 'location-list',
                        component: MultiTenantLocationListPage,
                    },
                ],
            },
            {
                path: 'l/:locationIdCode',
                resolve: { location: locationResolver },
                canDeactivate: [clearLocationCanDeactivateGuard],
                children: [
                    { path: '', redirectTo: 'location-home', pathMatch: 'full' },
                    {
                        path: 'location-home',
                        component: LocationHomePage,
                    },
                    {
                        path: 'settings',
                        loadChildren: () => import('./location-settings.routes').then((r) => r.LocationSettingsRoutes),
                    },
                ],
            },
        ],
    },
    {
        path: 'dev-world',
        component: MainContainerComponent,
        children: [
            {
                path: '',
                loadChildren: () => import('../dev/dev.routes').then((r) => r.DevRoutes),
            },
        ],
    },
    {
        path: 'set-debug',
        component: SetDebugSettingsPage,
    },
    // Needs to be last route
    { path: '**', redirectTo: 'tenant-list' },
];
